import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import AssessmentTempEditContainer from './AssessmentTempEditContainer';
import DimensionList from './DimensionList';
import { subLanguageList } from '../../../../../utils/LanguageUtil';
import TemplateMetaData from './MetaData';
import ArrayUtil from '../../../../../utils/ArrayUtil';
import useSubscriptionExpiredRestrictions from '../../../../../hooks/useSubscriptionExpiredRestrictions';

const messages = defineMessages({
  edit: {
    id: 'actions.edit',
    defaultMessage: 'Edit',
  },
});

const Detail = ({ submitEditData, editing, closeEditing, editTemplate, languageList, data }) => {
  const { formatMessage } = useIntl();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const { account } = data;

  const { assessmentTemplate, assessmentSections, communicationTemplates } = account;

  const activeLocales = subLanguageList(assessmentTemplate.activeLocales, languageList);
  const activeReportLocales = subLanguageList(assessmentTemplate.reportActiveLocales, languageList);
  const activeJSPLocales = subLanguageList(assessmentTemplate.jpsActiveLocales, languageList);

  const {
    title,
    inviteTemplateId,
    thankYouTemplateId,
    reminderTemplateId,
    feedbackTemplateId,
    behaviorDimensions,
    cognitiveDimensions,
    interestsDimensions,
  } = assessmentTemplate;
  const initialValues = {
    title,
    inviteTemplateId: inviteTemplateId ? parseInt(inviteTemplateId, 10) : -1,
    thankYouTemplateId: thankYouTemplateId ? parseInt(thankYouTemplateId, 10) : -1,
    reminderTemplateId: reminderTemplateId ? parseInt(reminderTemplateId, 10) : -1,
    feedbackTemplateId: feedbackTemplateId ? parseInt(feedbackTemplateId, 10) : -1,
  };

  const sortedSupportedLocales = ArrayUtil.sortByProp(activeLocales, 'value');
  const sortedReportLocales = ArrayUtil.sortByProp(activeReportLocales, 'value');
  const sortedJPSLocales = ArrayUtil.sortByProp(activeJSPLocales, 'value');

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="p-grid">
          <div className="p-sm-12 p-md-6">
            <Panel
              header={
                <div className="p-grid p-nogutter">
                  <div className="p-col">{assessmentTemplate.title}</div>
                  {!editing && (
                    <div className="p-col-2 align-right">
                      <Button
                        type="button"
                        className="p-button-secondary"
                        icon="fa fa-pencil"
                        disabled={subscriptionIsExpired}
                        tooltip={formatMessage(messages.edit)}
                        tooltipOptions={{ position: 'bottom' }}
                        onClick={editTemplate}
                      />
                    </div>
                  )}
                </div>
              }
            >
              <AssessmentTempEditContainer
                initialValues={initialValues}
                editing={editing}
                communicationTemplates={communicationTemplates}
                closeEditForm={closeEditing}
                saveData={submitEditData}
                account={account}
              />
              {!editing && (
                <DimensionList
                  assessmentSections={assessmentSections}
                  behaviorDimensions={behaviorDimensions}
                  cognitiveDimensions={cognitiveDimensions}
                  interestsDimensions={interestsDimensions}
                />
              )}
            </Panel>
          </div>
          <div className="p-sm-12 p-md-6">
            <TemplateMetaData
              template={assessmentTemplate}
              activeLocales={sortedSupportedLocales}
              activeReportLocales={sortedReportLocales}
              activeJSPLocales={sortedJPSLocales}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

Detail.propTypes = {
  submitEditData: PropTypes.func.isRequired,
  editing: PropTypes.bool.isRequired,
  closeEditing: PropTypes.func.isRequired,
  editTemplate: PropTypes.func.isRequired,
  languageList: PropTypes.arrayOf(PropTypes.any).isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default Detail;
